/**
 * ============================================================================
 * TEMPORARY — DELETE THIS FILE WHEN THE BACKEND IS WIRED UP (#6073)
 * ============================================================================
 *
 * Stand-in for the work environment survey API. Every number, category,
 * question and participant below is sample content, carried over from the AMIL
 * prototype's `database/init.sql` so the ported screens can be reviewed against
 * the original before any of it is redesigned.
 *
 * The three services in this feature are the only consumers. Each of their
 * methods documents the endpoint that will replace its lookup here, so
 * removing this file means deleting the `of(...)` bodies and nothing else.
 *
 * The category and question text is Norwegian because it is customer content,
 * not UI copy: these are the actual questions Kuba's work environment survey
 * asks. Once the backend owns them they belong in the database with locale
 * support — they must not move into the i18n files as interface strings.
 *
 * Two data problems inherited from the prototype, both worth deciding on
 * before the backend is written:
 *
 *  1. Categories 1, 5, 6 and 7 are reverse-scaled. Their answer labels run
 *     from "Svært mye" (1) down to "Ikke i det hele tatt" (5), so a low number
 *     is the good outcome — the opposite of the agreement categories, where 5
 *     is good. AMIL averaged all of them together regardless, which makes the
 *     overall score and the best/worst comparison meaningless. The mock keeps
 *     the labels as they were; it does not reproduce the flawed aggregation.
 *  2. The seed gave categories 2, 3 and 4 the label "Litt enig" for both
 *     option 2 and option 4. Option 2 is written as "Litt uenig" below, which
 *     is plainly what was meant.
 */

import {
  BestWorstCategoryDto,
  CategoryDepartmentMeanDto,
  CategoryHistoryDto,
  CategoryMeanDto,
  CategoryQuestionScoresDto,
  DepartmentAverageDto,
  QuestionAverageScoreDto,
  ResponseRateDto,
  SurveyAverageDto,
  SurveyCategoryDto,
  SurveyInsightsDto
} from '../analysis/models';
import { SurveyDto, SurveyParticipantDto } from '../surveys/models';
import {
  RespondentCategoryDto,
  RespondentResponseSubmitDto,
  RespondentSurveyDto
} from '../respondent/models';

/** Answer labels for the categories scored by agreement (5 is the good end). */
const AGREEMENT_OPTIONS = [
  'Helt uenig',
  'Litt uenig',
  'Hverken enig eller uenig',
  'Litt enig',
  'Helt enig'
];

/** Answer labels for the exposure categories (1 is the good end). */
const EXPOSURE_OPTIONS = [
  'Svært mye',
  'Nokså mye',
  'Til en viss grad',
  'Bare litt',
  'Ikke i det hele tatt'
];

export const MOCK_CATEGORIES: SurveyCategoryDto[] = [
  {
    Id: 1,
    Name: 'Personlig utvikling',
    Description:
      'Den enkelte ansattes muligheter til å utvikle seg igjennom arbeidet.'
  },
  {
    Id: 2,
    Name: 'Sosialt samspill',
    Description: 'Om kommunikasjonen mellom mennesker på arbeidsplassen.'
  },
  {
    Id: 3,
    Name: 'Organisatoriske forhold',
    Description: 'Hvordan den ansatte opplever strukturen i virksomheten.'
  },
  {
    Id: 4,
    Name: 'Organisasjonskultur',
    Description:
      'Hvordan den ansatte opplever organisasjonskulturen i virksomheten.'
  },
  {
    Id: 5,
    Name: 'Arbeidsmiljø',
    Description:
      'Hvordan den ansatte opplever det fysiske og kjemiske miljøet på sin arbeidsplass.'
  },
  {
    Id: 6,
    Name: 'Inneklima',
    Description: 'Hvordan den ansatte opplever inneklimaet på sin arbeidsplass.'
  },
  {
    Id: 7,
    Name: 'Ergonomi',
    Description:
      'Hvordan den ansatte opplever ergonomiske faktorer på sin arbeidsplass.'
  }
];

/** Which answer labels each category uses. */
export const MOCK_CATEGORY_OPTIONS: Record<number, string[]> = {
  1: EXPOSURE_OPTIONS,
  2: AGREEMENT_OPTIONS,
  3: AGREEMENT_OPTIONS,
  4: AGREEMENT_OPTIONS,
  5: EXPOSURE_OPTIONS,
  6: EXPOSURE_OPTIONS,
  7: EXPOSURE_OPTIONS
};

interface MockQuestion {
  Id: number;
  CategoryId: number;
  Text: string;
}

/** The 43 seeded questions, in category order. */
export const MOCK_QUESTIONS: MockQuestion[] = [
  // Personlig utvikling
  { Id: 1, CategoryId: 1, Text: 'Mitt arbeid er utfordrende på en positiv måte.' },
  { Id: 2, CategoryId: 1, Text: 'Jeg kan påvirke mengden arbeid som blir tildelt meg.' },
  { Id: 3, CategoryId: 1, Text: 'Jeg kan påvirke beslutninger som er viktige for mitt arbeid.' },
  { Id: 4, CategoryId: 1, Text: 'Jeg er fornøyd med kvaliteten på arbeidet som jeg utfører.' },
  { Id: 5, CategoryId: 1, Text: 'Jeg får informasjon om kvaliteten på arbeidet jeg utfører.' },
  { Id: 6, CategoryId: 1, Text: 'Jeg har ikke for mye å gjøre.' },
  { Id: 7, CategoryId: 1, Text: 'Jeg utfører ikke oppgaver jeg trenger mer opplæring for å gjøre.' },
  {
    Id: 8,
    CategoryId: 1,
    Text:
      '"Stress" innebærer en situasjon der en person føler seg anspent, urolig, nervøs eller engstelig, eller ikke er i stand til å sove om natten, fordi deres tanker er opprørt hele tiden. Jeg føler ikke denne typen stress nå for tiden.'
  },
  { Id: 9, CategoryId: 1, Text: 'Alt i alt, er jeg fornøyd med min jobb.' },
  { Id: 10, CategoryId: 1, Text: 'Jeg har hatt helseplager som jeg mener skyldes dårlig trivsel.' },

  // Sosialt samspill
  { Id: 11, CategoryId: 2, Text: 'Om jeg trenger det, kan jeg få støtte og hjelp i mitt arbeid fra mine kollegaer.' },
  { Id: 12, CategoryId: 2, Text: 'Om jeg trenger det, kan jeg få støtte og hjelp i mitt arbeid fra min nærmeste leder.' },
  { Id: 13, CategoryId: 2, Text: 'Mine arbeidsresultater blir verdsatt av min nærmeste leder.' },
  { Id: 14, CategoryId: 2, Text: 'Om jeg trenger det, er min nærmeste leder villig til å lytte når jeg har problemer i arbeidet.' },
  { Id: 15, CategoryId: 2, Text: 'Når konflikter oppstår, blir de behandlet på en god måte.' },
  { Id: 16, CategoryId: 2, Text: 'Jeg har ikke lagt merke til forstyrrende konflikter mellom arbeidskollegaer.' },
  { Id: 17, CategoryId: 2, Text: 'Jeg føler meg aldri ille til mote eller nedfor på grunn av kritikk eller vanskeligheter på jobben.' },
  {
    Id: 18,
    CategoryId: 2,
    Text:
      'Jeg har ikke lagt merke til om noen på min arbeidsplass er blitt utsatt for mobbing eller trakassering (i egen avdeling) i løpet av de siste 6 måneder.'
  },
  { Id: 19, CategoryId: 2, Text: 'Forholdet mitt til mine arbeidskolleger er godt.' },
  { Id: 20, CategoryId: 2, Text: 'Forholdet mitt til min nærmeste leder er godt.' },

  // Organisatoriske forhold
  { Id: 21, CategoryId: 3, Text: 'Min nærmeste leder hjelper meg med å utvikle mine ferdigheter.' },
  { Id: 22, CategoryId: 3, Text: 'Det er fastsatt klare mål for min jobb.' },
  { Id: 23, CategoryId: 3, Text: 'Jeg stoler på ledelsens evne til å ivareta arbeidsplassens fremtid.' },
  { Id: 24, CategoryId: 3, Text: 'Jeg får ikke oppgaver uten tilstrekkelige hjelpemidler og ressurser for å utføre dem.' },
  { Id: 25, CategoryId: 3, Text: 'Forholdet mitt til bedriftens ledelse er godt.' },

  // Organisasjonskultur
  { Id: 26, CategoryId: 4, Text: 'Denne organisasjonen inspirerer meg virkelig til å gjøre mitt beste.' },
  { Id: 27, CategoryId: 4, Text: 'Jeg sier til mine venner at dette er en god organisasjon å jobbe i.' },
  { Id: 28, CategoryId: 4, Text: 'De ansatte blir oppmuntret til å tenke ut måter å gjøre ting bedre på mitt arbeidssted.' },
  { Id: 29, CategoryId: 4, Text: 'Det er god kommunikasjon i min avdeling.' },
  { Id: 30, CategoryId: 4, Text: 'Ledelsen på min arbeidsplass er opptatt av den ansattes helse og velvære.' },
  { Id: 31, CategoryId: 4, Text: 'Jeg får belønning for velgjort arbeid (penger, oppmuntring).' },

  // Arbeidsmiljø
  { Id: 32, CategoryId: 5, Text: 'Er du utsatt for helsefarlige kjemikalier/stoffer på arbeidsplassen?' },
  { Id: 33, CategoryId: 5, Text: 'Er du utsatt for sjenerende støy på arbeidsplassen?' },
  { Id: 34, CategoryId: 5, Text: 'Er du utsatt for forurensninger som støv, partikler eller fibre på arbeidsplassen?' },
  {
    Id: 35,
    CategoryId: 5,
    Text:
      'Er du utsatt for vibrasjoner i hånd/arm eller såkalt helkropps-vibrasjoner på jobben? (Håndholdt verktøy, maskiner, kjøretøy, etc.)'
  },
  { Id: 36, CategoryId: 5, Text: 'Har du det siste året hatt helseplager som du mener skyldes slike faktorer?' },

  // Inneklima
  { Id: 37, CategoryId: 6, Text: 'Opplever du dårlig luftkvalitet på arbeidsplassen din?' },
  { Id: 38, CategoryId: 6, Text: 'Opplever du dårlig renhold på jobben din?' },
  { Id: 39, CategoryId: 6, Text: 'Opplever du dårlig belysning på arbeidsplassen din?' },
  { Id: 40, CategoryId: 6, Text: 'Har du det siste året hatt helseplager som du mener skyldes dårlig inneklima?' },

  // Ergonomi
  { Id: 41, CategoryId: 7, Text: 'Har du ensidige arbeidsstillinger i løpet av dagen?' },
  { Id: 42, CategoryId: 7, Text: 'Har du tunge løft (over 15 kg) i løpet av dagen?' },
  {
    Id: 43,
    CategoryId: 7,
    Text:
      'Har du det siste året hatt muskel/skjelettplager som du mener skyldes dårlig forhold i ditt arbeidsmiljø?'
  }
];

/**
 * Candidate participants. Stands in for the business's active employees, which
 * is where the real list comes from — AMIL's separate participant register is
 * not carried over.
 */
export const MOCK_PARTICIPANTS: SurveyParticipantDto[] = [
  { Id: 1, Name: 'Rolf Thorsen', Email: 'rolf.thorsen@example.com', DepartmentId: 1, DepartmentName: 'Produksjon' },
  { Id: 2, Name: 'Bob Myhre Stang', Email: 'bob.stang@example.com', DepartmentId: 1, DepartmentName: 'Produksjon' },
  { Id: 3, Name: 'Magdalena Krus', Email: 'magdalena.krus@example.com', DepartmentId: 1, DepartmentName: 'Produksjon' },
  { Id: 4, Name: 'Eirik Bjørndal', Email: 'eirik.bjorndal@example.com', DepartmentId: 2, DepartmentName: 'Regnskap' },
  { Id: 5, Name: 'Lukas Formoe', Email: 'lukas.formoe@example.com', DepartmentId: 2, DepartmentName: 'Regnskap' },
  { Id: 6, Name: 'Ole Krister Hansen', Email: 'ole.hansen@example.com', DepartmentId: 3, DepartmentName: 'Lager' },
  { Id: 7, Name: 'Lillian Nordmann', Email: 'lillian.nordmann@example.com', DepartmentId: 3, DepartmentName: 'Lager' },
  { Id: 8, Name: 'Arne Østrem', Email: 'arne.ostrem@example.com', DepartmentId: 3, DepartmentName: 'Lager' },
  { Id: 9, Name: 'Viggo Torgersen', Email: 'viggo.torgersen@example.com', DepartmentId: 4, DepartmentName: 'Administrasjon' },
  { Id: 10, Name: 'Hanne Monsen', Email: 'hanne.monsen@example.com', DepartmentId: 4, DepartmentName: 'Administrasjon' },
  { Id: 11, Name: 'Jenny Winther', Email: 'jenny.winther@example.com', DepartmentId: 4, DepartmentName: 'Administrasjon' },
  { Id: 12, Name: 'Ola Nordmann', Email: 'ola.nordmann@example.com', DepartmentId: null, DepartmentName: '' }
];

/** Three surveys: two finished, one currently open. */
export const MOCK_SURVEYS: SurveyDto[] = [
  {
    Id: 1,
    BusinessId: 0,
    Title: 'Arbeidsmiljøundersøkelse 2024',
    StartDate: '2024-09-02',
    EndDate: '2024-09-20',
    ReminderDate: '2024-09-13',
    ParticipantIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  },
  {
    Id: 2,
    BusinessId: 0,
    Title: 'Arbeidsmiljøundersøkelse 2025',
    StartDate: '2025-09-01',
    EndDate: '2025-09-19',
    ReminderDate: '2025-09-12',
    ParticipantIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
  },
  {
    Id: 3,
    BusinessId: 0,
    Title: 'Arbeidsmiljøundersøkelse 2026 Q1',
    StartDate: '2026-02-02',
    EndDate: '2026-02-20',
    ReminderDate: '2026-02-13',
    ParticipantIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  }
];

/** Date each survey's result is plotted at on the history line. */
const MOCK_SURVEY_DATES: Record<number, string> = {
  1: '2024-09-20',
  2: '2025-09-19',
  3: '2026-02-20'
};

/** Overall average per survey, on the 1-5 scale. */
const MOCK_SURVEY_AVERAGES: Record<number, number> = {
  1: 3.4,
  2: 3.8,
  3: 4.1
};

/** Department averages per survey. */
const MOCK_DEPARTMENT_AVERAGES: Record<number, DepartmentAverageDto[]> = {
  1: [
    { DepartmentName: 'Produksjon', AverageScore: 3.1 },
    { DepartmentName: 'Regnskap', AverageScore: 3.6 },
    { DepartmentName: 'Lager', AverageScore: 3.2 },
    { DepartmentName: 'Administrasjon', AverageScore: 3.8 }
  ],
  2: [
    { DepartmentName: 'Produksjon', AverageScore: 3.5 },
    { DepartmentName: 'Regnskap', AverageScore: 4.0 },
    { DepartmentName: 'Lager', AverageScore: 3.4 },
    { DepartmentName: 'Administrasjon', AverageScore: 4.2 }
  ],
  3: [
    { DepartmentName: 'Produksjon', AverageScore: 3.9 },
    { DepartmentName: 'Regnskap', AverageScore: 4.3 },
    { DepartmentName: 'Lager', AverageScore: 3.6 },
    { DepartmentName: 'Administrasjon', AverageScore: 4.5 }
  ]
};

/** Response rate per survey. */
const MOCK_RESPONSE_RATES: Record<number, ResponseRateDto> = {
  1: { SurveyId: 1, ResponseRatePercent: 80, RespondedCount: 8, ParticipantCount: 10 },
  2: { SurveyId: 2, ResponseRatePercent: 91, RespondedCount: 10, ParticipantCount: 11 },
  3: { SurveyId: 3, ResponseRatePercent: 42, RespondedCount: 5, ParticipantCount: 12 }
};

/**
 * Category means per survey, keyed `surveyId:categoryId`. Spread across the
 * red, yellow and green bands so the gauges show all three states.
 */
const MOCK_CATEGORY_MEANS: Record<string, number> = {
  '1:1': 3.0, '1:2': 3.9, '1:3': 2.8, '1:4': 3.3, '1:5': 4.1, '1:6': 2.4, '1:7': 3.5,
  '2:1': 3.4, '2:2': 4.1, '2:3': 3.1, '2:4': 3.7, '2:5': 4.3, '2:6': 2.9, '2:7': 3.8,
  '3:1': 3.8, '3:2': 4.4, '3:3': 3.5, '3:4': 4.0, '3:5': 4.5, '3:6': 3.2, '3:7': 4.1
};

/** Department means for one category, keyed `surveyId:categoryId`. */
const MOCK_CATEGORY_DEPARTMENT_MEANS: Record<string, CategoryDepartmentMeanDto[]> = {};
for (const survey of MOCK_SURVEYS) {
  for (const category of MOCK_CATEGORIES) {
    const base = MOCK_CATEGORY_MEANS[`${survey.Id}:${category.Id}`] ?? 3.5;
    MOCK_CATEGORY_DEPARTMENT_MEANS[`${survey.Id}:${category.Id}`] = [
      { DepartmentId: 1, DepartmentName: 'Produksjon', MeanValue: round1(base - 0.4) },
      { DepartmentId: 2, DepartmentName: 'Regnskap', MeanValue: round1(base + 0.2) },
      { DepartmentId: 3, DepartmentName: 'Lager', MeanValue: round1(base - 0.2) },
      { DepartmentId: 4, DepartmentName: 'Administrasjon', MeanValue: round1(base + 0.4) }
    ];
  }
}

export const MOCK_INSIGHTS: SurveyInsightsDto = {
  BenchmarkIndex: 3.6,
  HonestyScore: 4.2,
  Suggestions: [
    'Inneklima skårer lavest av alle kategorier, og har gjort det i tre undersøkelser på rad. Start med en kartlegging av luftkvalitet og renhold i produksjonslokalet.',
    'Produksjon ligger konsekvent under de andre avdelingene. Ta opp arbeidsmengde og opplæring på neste avdelingsmøte.'
  ],
  Comments: [
    'Jeg har fått et inntrykk av at ideene mine ofte blir avvist, og det er demotiverende.',
    'Det er blitt merkbart bedre kommunikasjon i avdelingen etter omorganiseringen.'
  ]
};

/** Rounds to one decimal, the precision every score is displayed at. */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Deterministic per-question score derived from the question id and the
 * category mean, so the question list stays stable across reloads instead of
 * reshuffling on every render.
 */
function questionScore(surveyId: number, question: MockQuestion): number {
  const base = MOCK_CATEGORY_MEANS[`${surveyId}:${question.CategoryId}`] ?? 3.5;
  const spread = ((question.Id * 7) % 11) / 10 - 0.5;
  return round1(Math.min(5, Math.max(1, base + spread)));
}

/**
 * The sample surveys, presented as belonging to the business that asked for
 * them. Surveys created during the session already carry their real business.
 */
export function getMockSurveys(businessId: number): SurveyDto[] {
  return MOCK_SURVEYS.map(survey => ({
    ...survey,
    BusinessId: survey.BusinessId === 0 ? businessId : survey.BusinessId
  }));
}

/**
 * Sample employees, as candidate participants.
 *
 * `businessId` is accepted so the call site matches the endpoint that will
 * replace it; the sample set is the same whichever business asks.
 */
export function getMockParticipants(businessId: number): SurveyParticipantDto[] {
  return MOCK_PARTICIPANTS;
}

/**
 * The categories a survey covers.
 *
 * Every sample survey covers all seven; a real survey selects a subset, which
 * is why the id is part of the signature.
 */
export function getMockCategories(surveyId: number): SurveyCategoryDto[] {
  return MOCK_CATEGORIES;
}

/**
 * Placeholder insight panels.
 *
 * Nothing is computed per survey yet, so the id is accepted and ignored.
 */
export function getMockInsights(surveyId: number): SurveyInsightsDto {
  return MOCK_INSIGHTS;
}

export function getMockSurveyAverage(surveyId: number): SurveyAverageDto {
  return {
    SurveyId: surveyId,
    SurveyDate: MOCK_SURVEY_DATES[surveyId] ?? null,
    AverageScore: MOCK_SURVEY_AVERAGES[surveyId] ?? 0
  };
}

/**
 * Every survey's overall score, oldest first. `businessId` is accepted to
 * match the endpoint; the sample history is the same for every business.
 */
export function getMockHistoricalAverages(businessId: number): SurveyAverageDto[] {
  return MOCK_SURVEYS.map(survey => getMockSurveyAverage(survey.Id));
}

export function getMockDepartmentAverages(surveyId: number): DepartmentAverageDto[] {
  return MOCK_DEPARTMENT_AVERAGES[surveyId] ?? [];
}

export function getMockResponseRate(surveyId: number): ResponseRateDto {
  return (
    MOCK_RESPONSE_RATES[surveyId] ?? {
      SurveyId: surveyId,
      ResponseRatePercent: 0,
      RespondedCount: 0,
      ParticipantCount: 0
    }
  );
}

export function getMockCategoryMean(
  surveyId: number,
  categoryId: number
): CategoryMeanDto {
  return {
    CategoryId: categoryId,
    MeanValue: MOCK_CATEGORY_MEANS[`${surveyId}:${categoryId}`] ?? 0
  };
}

export function getMockCategoryDepartmentMeans(
  surveyId: number,
  categoryId: number
): CategoryDepartmentMeanDto[] {
  return MOCK_CATEGORY_DEPARTMENT_MEANS[`${surveyId}:${categoryId}`] ?? [];
}

/**
 * A category's score across every survey. `businessId` is accepted to match
 * the endpoint; the sample history is the same for every business.
 */
export function getMockCategoryHistory(
  businessId: number,
  categoryId: number
): CategoryHistoryDto {
  const points = MOCK_SURVEYS.map(survey => ({
    DateForScore: MOCK_SURVEY_DATES[survey.Id],
    AverageScore: MOCK_CATEGORY_MEANS[`${survey.Id}:${categoryId}`] ?? 0
  }));

  const total = points.reduce((sum, point) => sum + point.AverageScore, 0);

  return {
    CategoryId: categoryId,
    Points: points,
    TotalAverageScore: points.length ? round1(total / points.length) : 0
  };
}

export function getMockCategoryQuestionScores(
  surveyId: number,
  categoryId: number
): CategoryQuestionScoresDto {
  const questions: QuestionAverageScoreDto[] = MOCK_QUESTIONS.filter(
    question => question.CategoryId === categoryId
  ).map(question => ({
    QuestionId: question.Id,
    Text: question.Text,
    AverageScore: questionScore(surveyId, question)
  }));

  return { CategoryId: categoryId, Questions: questions };
}

export function getMockBestAndWorstCategories(
  surveyId: number
): BestWorstCategoryDto {
  const scored = MOCK_CATEGORIES.map(category => ({
    CategoryId: category.Id,
    CategoryName: category.Name,
    AverageScore: MOCK_CATEGORY_MEANS[`${surveyId}:${category.Id}`] ?? 0
  })).filter(category => category.AverageScore > 0);

  if (!scored.length) {
    return { BestCategory: null, WorstCategory: null };
  }

  const ascending = [...scored].sort((a, b) => a.AverageScore - b.AverageScore);

  return {
    BestCategory: ascending[ascending.length - 1],
    WorstCategory: ascending[0]
  };
}

/**
 * Answers submitted during this browser session, by respondent token and then
 * question id. Lives in memory only, so a reload starts over — enough to see
 * that answers come back when the respondent revisits a category, which is
 * what the real per-category save is for.
 */
const MOCK_RESPONSES: Record<string, Record<number, number>> = {};

/** Records one category's answers for a respondent. */
export function saveMockRespondentResponses(
  respondentId: string,
  responses: RespondentResponseSubmitDto[]
): void {
  const existing = MOCK_RESPONSES[respondentId];
  const saved = existing === undefined ? {} : existing;

  responses.forEach(response => {
    saved[response.QuestionId] = response.Response;
  });

  MOCK_RESPONSES[respondentId] = saved;
}

/**
 * Prefix of the stand-in respondent tokens the survey list hands out, followed
 * by the survey id: `preview-3`.
 *
 * A real token is an opaque per-respondent GUID that says nothing about which
 * survey it belongs to — the backend looks that up. Until there is a backend,
 * the survey id is carried in the token so opening a survey from the list
 * shows that survey rather than an arbitrary one.
 *
 * Both this constant and `buildMockRespondentToken` go away with the mock; the
 * survey list will then hand out the tokens the backend generated.
 */
export const MOCK_RESPONDENT_TOKEN_PREFIX = 'preview-';

/** The stand-in token for answering a given survey. */
export function buildMockRespondentToken(surveyId: number): string {
  return `${MOCK_RESPONDENT_TOKEN_PREFIX}${surveyId}`;
}

/**
 * Reads the survey out of a stand-in token, falling back to the newest survey
 * for a token that came from somewhere else (a hand-typed URL, say).
 */
function resolveMockSurvey(respondentId: string): SurveyDto | null {
  if (respondentId.indexOf(MOCK_RESPONDENT_TOKEN_PREFIX) === 0) {
    const surveyId = Number(
      respondentId.substring(MOCK_RESPONDENT_TOKEN_PREFIX.length)
    );
    const matches = MOCK_SURVEYS.filter(survey => survey.Id === surveyId);

    if (matches.length > 0) {
      return matches[0];
    }
  }

  return MOCK_SURVEYS.length > 0 ? MOCK_SURVEYS[MOCK_SURVEYS.length - 1] : null;
}

/**
 * The survey as a respondent sees it, with whatever they have already
 * answered in this session.
 */
export function getMockRespondentSurvey(respondentId: string): RespondentSurveyDto {
  const storedAnswers = MOCK_RESPONSES[respondentId];
  const saved = storedAnswers === undefined ? {} : storedAnswers;
  const survey = resolveMockSurvey(respondentId);

  const categories: RespondentCategoryDto[] = MOCK_CATEGORIES.map(category => {
    const options = MOCK_CATEGORY_OPTIONS[category.Id];

    return {
      Id: category.Id,
      Title: category.Name,
      Description: category.Description,
      Options: options === undefined ? AGREEMENT_OPTIONS : options,
      Questions: MOCK_QUESTIONS.filter(
        question => question.CategoryId === category.Id
      ).map(question => ({
        Id: question.Id,
        Text: question.Text,
        Response: saved[question.Id] === undefined ? null : saved[question.Id]
      }))
    };
  });

  return {
    RespondentId: respondentId,
    SurveyId: survey === null ? 0 : survey.Id,
    Title: survey === null ? '' : survey.Title,
    BusinessName: 'Kuba Norge AS',
    Categories: categories
  };
}
